import React, { useState, useEffect, useMemo, useRef } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import DocumentationService from '../../services/DocumentationService';
import toast from 'react-hot-toast';
import { FiSearch, FiCheckSquare, FiSquare, FiChevronDown, FiChevronUp, FiUsers, FiXCircle, FiTrash2, FiAlertTriangle, FiPlus, FiEdit2 } from 'react-icons/fi';

function DocumentationForm({ onRefresh, editData, onCancel }) {
  const [modules, setModules] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [rawDocTypes, setRawDocTypes] = useState([]); // Keep original for modal
  const [empSearch, setEmpSearch] = useState('');
  const [isEmpOpen, setIsEmpOpen] = useState(false);
  const empMenuRef = useRef(null);
  
  // Add Type Modal state
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [addingType, setAddingType] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState(null);
  
  const [formData, setFormData] = useState({
    module: null,
    title: '',
    doc_type: null,
    customers: [],
    viewers: [], 
    description: '',
    version: '',
    video_url: '',
    status: 'Active'
  });
  
  const [files, setFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Attachment delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);

  // Type delete confirmation
  const [showDeleteTypeConfirm, setShowDeleteTypeConfirm] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);

  useEffect(() => {
    fetchCustomers();
    fetchAllModules();
    fetchEmployees();
    fetchDocTypes();
    
    // Outside click listener to close employee dropdown
    const handleClickOutside = (event) => {
      if (empMenuRef.current && !empMenuRef.current.contains(event.target)) {
        setIsEmpOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (editData) {
      setFormData({
        module: editData.module ? { value: editData.module, label: editData.module } : null,
        title: editData.title || '',
        doc_type: editData.doc_type ? { value: editData.doc_type, label: editData.doc_type } : null,
        // FIX: Look up customer names instead of hardcoding 'Customer ID'
        customers: (editData.customers || '').split(',').filter(id => id).map(cId => {
           const found = customers.find(c => c.value === String(cId));
           return { value: cId, label: found ? found.label : `Customer ${cId}` };
        }), 
        viewers: (editData.accessible_to || '').split(',').filter(id => id),
        description: editData.description || '',
        version: editData.version || '',
        video_url: editData.video_url || '',
        status: editData.status === 'Active' ? 'Active' : 'Inactive'
      });
      // Set existing attachments
      setExistingAttachments(editData.attachments || []);
    }
  }, [editData, customers, modules]);

  const fetchCustomers = async () => {
    try {
      const resp = await DocumentationService.getCustomers();
      if (resp.status) {
        setCustomers(resp.data.map(c => ({
          value: String(c.cust_key || c.child_id),
          label: c.customer_name || 'Unknown'
        })));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchAllModules = async () => {
    try {
      const resp = await DocumentationService.getAllModules();
      if (resp.status) {
        setModules(resp.data.map(m => ({ value: m.module_name, label: m.module_name })));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchDocTypes = async () => {
    try {
      const resp = await DocumentationService.getDocTypes();
      if (Array.isArray(resp)) {
        setRawDocTypes(resp);
        setDocTypes(resp.map(t => ({ value: t.type_name, label: t.type_name })));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchEmployees = async () => {
    try {
      const resp = await DocumentationService.getEmployees();
      if (resp.status) {
        setEmployees(resp.data.map(emp => ({
          value: String(emp.user_id),
          label: `${emp.first_name} ${emp.last_name} (${emp.designation_name})`
        })));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveDocType = async () => {
    if (!newTypeName.trim()) {
      toast.error('Please enter a type name');
      return;
    }
    setAddingType(true);
    try {
      let resp;
      if (editingTypeId) {
        resp = await DocumentationService.updateDocType(editingTypeId, newTypeName.trim());
      } else {
        resp = await DocumentationService.createDocType(newTypeName.trim());
      }

      if (resp.status) {
        toast.success(editingTypeId ? 'Type updated' : 'New type added');
        fetchDocTypes(); // Refresh list
        setNewTypeName('');
        setEditingTypeId(null);
      } else {
        toast.error(resp.message || 'Failed to save type');
      }
    } catch (e) {
      toast.error('Error saving type');
    } finally {
      setAddingType(false);
    }
  };

  const handleDeleteClickForType = (type) => {
    setTypeToDelete(type);
    setShowDeleteTypeConfirm(true);
  };

  const confirmTypeDeletion = async () => {
    if (!typeToDelete) return;
    try {
      const resp = await DocumentationService.deleteDocType(typeToDelete.id);
      if (resp.status) {
        toast.success('Type deleted');
        fetchDocTypes();
        setShowDeleteTypeConfirm(false);
        setTypeToDelete(null);
      }
    } catch (e) {
      toast.error('Error deleting type');
    }
  };

  const filteredEmployees = useMemo(() => {
    if (!empSearch.trim()) return employees;
    return employees.filter(emp => 
      emp.label.toLowerCase().includes(empSearch.toLowerCase())
    );
  }, [employees, empSearch]);

  const handleToggleViewer = (id) => {
    setFormData(prev => {
      const viewers = [...prev.viewers];
      const index = viewers.indexOf(id);
      if (index === -1) {
        viewers.push(id);
      } else {
        viewers.splice(index, 1);
      }
      return { ...prev, viewers };
    });
  };

  const handleSelectAll = (e) => {
    e.stopPropagation();
    setFormData(prev => ({
      ...prev,
      viewers: employees.map(emp => emp.value)
    }));
  };

  const handleDeselectAll = (e) => {
    e.stopPropagation();
    setFormData(prev => ({
      ...prev,
      viewers: []
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      const allowed = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'zip', 'mp4', 'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'];
      const maxLimit = 4 * 1024 * 1024; // 4MB

      if (!allowed.includes(ext)) {
        toast.error(`Invalid format: ${file.name}`);
        return false;
      }
      if (file.size > maxLimit) {
        toast.error(`File exceeds 4MB limit: ${file.name}`);
        return false;
      }
      return true;
    });

    setFiles(prev => [...prev, ...validFiles]);
  };

  const handleDeleteClick = (att) => {
    setAttachmentToDelete(att);
    setShowDeleteConfirm(true);
  };

  const confirmAttachmentDeletion = async () => {
    if (!attachmentToDelete) return;
    try {
      const resp = await DocumentationService.deleteAttachment(attachmentToDelete.id);
      if (resp.status) {
        toast.success('File removed successfully');
        setExistingAttachments(prev => prev.filter(att => att.id !== attachmentToDelete.id));
        setShowDeleteConfirm(false);
        setAttachmentToDelete(null);
        if (onRefresh) onRefresh();
      } else {
        toast.error(resp.message || 'Failed to remove file');
      }
    } catch (err) {
      toast.error('Error removing file');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.module || !formData.doc_type || formData.customers.length === 0 || !formData.title.trim()) {
      toast.error('Please fill required fields (Module, Title, Type, Customers)');
      return;
    }

    setUploading(true);
    setUploadProgress(10);
    const formPayload = new FormData();
    
    formPayload.append('module', formData.module.value);
    formPayload.append('title', formData.title);
    formPayload.append('doc_type', formData.doc_type.value);
    formPayload.append('customers', formData.customers.map(c => c.value).join(','));
    formPayload.append('accessible_to', formData.viewers.join(','));
    formPayload.append('description', formData.description);
    formPayload.append('version', formData.version);
    formPayload.append('video_url', formData.video_url);
    formPayload.append('status', formData.status);
    
    files.forEach(file => {
      formPayload.append('files[]', file);
    });

    setUploadProgress(40);
    try {
      let resp;
      if (editData) {
        resp = await DocumentationService.updateDoc(editData.id, formPayload);
      } else {
        resp = await DocumentationService.createDoc(formPayload);
      }

      if (resp.status) {
        console.log("Upload Success - Remote URL(s):", resp);
        toast.success(resp.message || 'Saved successfully');
        setUploadProgress(100);
        if (onRefresh) onRefresh();
        if (onCancel) onCancel();
      } else {
        toast.error(resp.message || 'Error occurred');
      }
    } catch (err) {
      toast.error('Failed to save documentation');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 p-1">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        
        <div className="col-span-1 md:col-span-2">
           <label className="block text-sm font-semibold text-gray-700 mb-1">Title / Document Name <span className="text-red-500">*</span></label>
           <input 
             type="text" 
             className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-main focus:border-main outline-none"
             placeholder="e.g. Ticket Workflow Implementation Guide"
             value={formData.title} 
             required
             autoFocus
             onChange={(e) => setFormData({...formData, title: e.target.value})} 
           />
        </div>

        <div className="col-span-1">
           <label className="block text-sm font-semibold text-gray-700 mb-1">Module Name <span className="text-red-500">*</span></label>
           <CreatableSelect 
             options={modules} 
             value={formData.module}
             onChange={v => setFormData({...formData, module: v})}
             placeholder="Select or Type Module..."
             isClearable
           />
        </div>

        <div className="col-span-1">
           <div className="flex items-center gap-2 mb-1">
              <label className="block text-sm font-semibold text-gray-700">Documentation Type <span className="text-red-500">*</span></label>
              <button 
                type="button"
                onClick={() => setShowAddTypeModal(true)}
                className="flex items-center justify-center w-5 h-5 bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition-colors border border-red-200 shadow-sm shadow-red-50"
                title="Add New Type"
              >
                <FiPlus size={11} className="stroke-[3]" />
              </button>
           </div>
           <Select 
             options={docTypes} 
             value={formData.doc_type}
             onChange={v => setFormData({...formData, doc_type: v})}
             placeholder="Select Type..."
           />
        </div>

        <div className="col-span-1 md:col-span-2">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Associated Customers <span className="text-red-500">*</span></label>
          <Select 
            isMulti
            options={customers} 
            value={formData.customers}
            onChange={v => setFormData({...formData, customers: v})}
            placeholder="Select Customers..."
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>

        {/* Dropdown-style Checkbox based Selection for Employees */}
        <div className="col-span-1 md:col-span-2 relative" ref={empMenuRef}>
           <label className="block text-sm font-semibold text-gray-700 mb-1">Accessible To (Employees)</label>
           
           <div 
             onClick={() => setIsEmpOpen(!isEmpOpen)}
             className={`flex items-center justify-between w-full border border-gray-300 rounded-md shadow-sm p-2 cursor-pointer bg-white transition-all ${isEmpOpen ? 'ring-2 ring-main border-main' : 'hover:border-gray-400'}`}
           >
              <div className="flex items-center gap-2 overflow-hidden">
                 <FiUsers className="text-gray-400 shrink-0" />
                 {formData.viewers.length > 0 ? (
                    <div className="flex gap-1 overflow-hidden">
                       <span className="text-sm font-semibold text-main nowrap leading-none py-1 px-1">{formData.viewers.length} Selected</span>
                       <div className="flex gap-1">
                          {formData.viewers.slice(0, 2).map(vid => {
                             const e = employees.find(emp => emp.value === vid);
                             return e ? <span key={vid} className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 truncate max-w-[80px]">{e.label.split(' (')[0]}</span> : null;
                          })}
                          {formData.viewers.length > 2 && <span className="text-[10px] text-gray-400">+{formData.viewers.length - 2}</span>}
                       </div>
                    </div>
                 ) : (
                    <span className="text-sm text-gray-400">Everyone can view (Default)</span>
                 )}
              </div>
              <div className="flex items-center gap-2">
                 {formData.viewers.length > 0 && <FiXCircle className="text-gray-300 hover:text-red-500 p-0.5" onClick={handleDeselectAll} />}
                 {isEmpOpen ? <FiChevronUp className="text-gray-400" /> : <FiChevronDown className="text-gray-400" />}
              </div>
           </div>

           {isEmpOpen && (
             <div className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[100] animate-in slide-in-from-top-2">
                <div className="p-2 border-b bg-gray-50 flex items-center justify-between gap-3">
                   <div className="flex items-center gap-2 bg-white border rounded px-2 py-1 flex-1">
                      <FiSearch className="text-gray-400" />
                      <input 
                        type="text" 
                        autoFocus
                        className="bg-transparent border-none outline-none text-xs w-full"
                        placeholder="Search employees..."
                        value={empSearch}
                        onChange={(e) => setEmpSearch(e.target.value)}
                      />
                   </div>
                   <div className="flex gap-2">
                      <button 
                        type="button" 
                        onClick={handleSelectAll}
                        className="text-[10px] uppercase font-bold text-blue-600 hover:text-blue-800"
                      >
                        All
                      </button>
                      <button 
                        type="button" 
                        onClick={handleDeselectAll}
                        className="text-[10px] uppercase font-bold text-red-600 hover:text-red-800"
                      >
                        None
                      </button>
                   </div>
                </div>
                <div className="max-h-60 overflow-y-auto divide-y divide-gray-100">
                   {filteredEmployees.length === 0 ? (
                     <div className="p-4 text-center text-gray-400 text-sm italic">No entries found.</div>
                   ) : filteredEmployees.map(emp => {
                     const isSelected = formData.viewers.includes(emp.value);
                     return (
                       <label 
                         key={emp.value} 
                         className={`flex items-center gap-3 px-4 py-2 cursor-pointer transition-colors hover:bg-gray-50 ${isSelected ? 'bg-blue-50/50' : ''}`}
                       >
                          <input 
                            type="checkbox" 
                            className="hidden"
                            checked={isSelected}
                            onChange={() => handleToggleViewer(emp.value)}
                          />
                          {isSelected ? <FiCheckSquare className="text-main text-lg" /> : <FiSquare className="text-gray-300 text-lg" />}
                          <div className="flex flex-col">
                             <span className={`text-[12px] font-medium ${isSelected ? 'text-blue-900 border-b-0' : 'text-gray-700 border-b-0'}`}>{emp.label.split(' (')[0]}</span>
                             <span className="text-[9px] text-gray-500 uppercase leading-none">{emp.label.split(' (')[1]?.replace(')', '')}</span>
                          </div>
                       </label>
                     );
                   })}
                </div>
                <div className="p-1 px-4 border-t bg-gray-50 flex justify-end">
                   <p className="text-[10px] text-gray-400 italic">Only selected employees will see this record.</p>
                </div>
             </div>
           )}
        </div>

        <div className="col-span-1">
           <label className="block text-sm font-semibold text-gray-700 mb-1">Version Number (Optional)</label>
           <input 
             type="text" 
             className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-main focus:border-main outline-none"
             placeholder="e.g. v1.0"
             value={formData.version} 
             onChange={(e) => setFormData({...formData, version: e.target.value})} 
           />
        </div>

        <div className="col-span-1">
           <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
           <select 
             className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-main focus:border-main outline-none"
             value={formData.status}
             onChange={(e) => setFormData({...formData, status: e.target.value})}
           >
             <option value="Active">Active</option>
             <option value="Inactive">Inactive</option>
           </select>
        </div>

        <div className="md:col-span-2">
           <label className="block text-sm font-semibold text-gray-700 mb-1">External Video URL (Optional)</label>
           <input 
             type="url" 
             className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-main focus:border-main outline-none"
             placeholder="e.g. https://www.youtube.com/watch?v=... or direct mp4 link"
             value={formData.video_url} 
             onChange={(e) => setFormData({...formData, video_url: e.target.value})} 
           />
           <p className="text-[10px] text-gray-400 mt-1 uppercase">Supports YouTube, Vimeo, or direct video links</p>
        </div>

      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Description / Notes</label>
        <textarea 
          className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-main focus:border-main outline-none"
          rows="3"
          placeholder="Brief summary or purpose of the document..."
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
        ></textarea>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Attachments</label>
        
        {/* Render Existing Attachments First */}
        {existingAttachments.length > 0 && (
          <div className="mb-4 bg-blue-50/50 p-3 rounded-lg border border-blue-100">
             <p className="text-[10px] font-bold text-red-600 uppercase mb-2 tracking-wider">Already Uploaded ({existingAttachments.length})</p>
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {existingAttachments.map((att, i) => (
                  <div key={att.id} className="flex justify-between items-center bg-white px-2 py-1.5 rounded-md text-[11px] border shadow-sm group">
                    <span className="truncate flex-1 text-gray-700">{att.file_name}</span>
                     <div className="flex gap-2">
                       <a 
                         href={att.file_path} 
                         target="_blank" 
                         rel="noopener noreferrer" 
                         className="text-red-500 hover:text-red-700 font-medium"
                       >
                         View
                       </a>
                       <button 
                         type="button" 
                         onClick={() => handleDeleteClick(att)}
                         className="text-gray-400 hover:text-red-500 transition-colors"
                         title="Remove file"
                       >
                         <FiTrash2 size={13} />
                       </button>
                    </div>
                  </div>
                ))}
             </div>
          </div>
        )}

        <div className="flex items-center justify-center w-full">
            <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex flex-col items-center justify-center pt-2 pb-2">
                    <p className="text-sm text-gray-500 font-medium">Click to upload or drag files</p>
                    <p className="text-[10px] text-gray-400">PDF, IMAGE, DOC, XLS, MP4, ZIP (Max. 4MB)</p>
                </div>
                <input type="file" className="hidden" multiple onChange={handleFileChange} />
            </label>
        </div>
        {files.length > 0 && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            {files.map((f, i) => (
              <div key={i} className="flex justify-between items-center bg-gray-100 px-2 py-1 rounded text-xs border">
                <span className="truncate flex-1">{f.name}</span>
                <button type="button" onClick={() => setFiles(files.filter((_, idx) => idx !== i))} className="ml-2 text-red-500 font-bold">&times;</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {uploading && (
         <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
           <div className="bg-main h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
         </div>
      )}

      <div className="flex justify-end gap-3 pt-6 border-t mt-4">
        <button 
          type="button"
          className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          disabled={uploading}
          className="px-8 py-2 text-sm font-semibold text-white bg-main rounded-md shadow-lg hover:bg-opacity-90 disabled:opacity-50 transition-all transform active:scale-95"
        >
          {uploading ? 'Processing...' : editData ? 'Update Documentation' : 'Create Documentation'}
        </button>
      </div>

      {/* Delete Confirmation Modal for Attachments */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[250] animate-in fade-in duration-200 backdrop-blur-[1px]">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 transform animate-in slide-in-from-bottom-4 duration-300 border-none">
            <div className="flex items-center gap-3 mb-4">
               <div className="bg-red-100 p-2 rounded-full">
                  <FiAlertTriangle className="text-red-600 text-xl" />
               </div>
               <h3 className="text-lg font-bold text-gray-900 leading-tight">Delete Attachment?</h3>
            </div>
            <p className="text-gray-500 mb-6 text-xs leading-relaxed">
              Are you sure you want to remove <span className="font-bold text-gray-700">"{attachmentToDelete?.file_name}"</span>? 
              This file will be permanently deleted from the storage server.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                   setShowDeleteConfirm(false);
                   setAttachmentToDelete(null);
                }}
                className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold text-xs transition-colors"
              >
                Keep File
              </button>
              <button
                type="button"
                onClick={confirmAttachmentDeletion}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold text-xs shadow-md shadow-red-100 transition-all active:scale-95"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Add/Manage Types Modal */}
      {showAddTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[260] animate-in fade-in duration-200 backdrop-blur-[1px]">
          <div className="bg-white rounded-xl shadow-2xl p-0 max-w-xl w-full mx-4 transform animate-in slide-in-from-bottom-4 duration-300 border-none overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gray-50 px-6 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                 <div className="bg-red-100 p-2 rounded-full">
                    <FiPlus className="text-red-600 text-xl" />
                 </div>
                 <h3 className="text-lg font-bold text-gray-900 leading-tight">Manage Document Types</h3>
              </div>
              <button 
                onClick={() => {
                  setShowAddTypeModal(false);
                  setEditingTypeId(null);
                  setNewTypeName('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiXCircle size={24} />
              </button>
            </div>
            
            <div className="p-6">
              {/* Add/Edit Input */}
              <div className="mb-8 bg-red-50 p-4 rounded-lg flex items-end gap-3 border border-red-100">
                 <div className="flex-1">
                    <label className="block text-[10px] uppercase font-bold text-red-600 mb-1">
                      {editingTypeId ? 'Edit Type' : 'Add New Category'}
                    </label>
                    <input 
                      type="text"
                      autoFocus
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-main outline-none bg-white shadow-inner"
                      placeholder="e.g. User Manual"
                      value={newTypeName}
                      onChange={(e) => setNewTypeName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSaveDocType()}
                    />
                 </div>
                 <div className="flex gap-2">
                    {editingTypeId && (
                      <button
                        type="button"
                        onClick={() => { setEditingTypeId(null); setNewTypeName(''); }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-xs font-bold hover:bg-gray-300 transition-all"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleSaveDocType}
                      disabled={addingType}
                      className="px-6 py-2 bg-main text-white rounded-lg hover:bg-opacity-90 font-bold text-xs shadow-md transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
                    >
                      {addingType ? 'Saving...' : editingTypeId ? 'Update Type' : 'Add Type'}
                    </button>
                 </div>
              </div>

              {/* List Table */}
              <div className="border rounded-lg overflow-hidden shadow-sm">
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 border-b sticky top-0">
                       <tr className="text-gray-500 uppercase font-bold tracking-tight">
                         <th className="px-4 py-3 text-left">Type Name</th>
                         <th className="px-4 py-3 text-right">Actions</th>
                       </tr>
                    </thead>
                    <tbody className="divide-y">
                      {rawDocTypes.length === 0 ? (
                        <tr><td colSpan="2" className="px-4 py-8 text-center text-gray-400 italic">No types defined. Add one above.</td></tr>
                      ) : rawDocTypes.map(type => (
                        <tr key={type.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-4 py-3 font-semibold text-gray-700">{type.type_name}</td>
                          <td className="px-4 py-3 text-right">
                             <div className="flex justify-end gap-2">
                                <button 
                                  type="button"
                                  onClick={() => { setEditingTypeId(type.id); setNewTypeName(type.type_name); }}
                                  className="p-1.5 text-orange-500 hover:bg-orange-50 rounded transition-colors"
                                  title="Edit"
                                >
                                  <FiEdit2 size={14} />
                                </button>
                                <button 
                                  type="button"
                                  onClick={() => handleDeleteClickForType(type)}
                                  className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                                  title="Delete"
                                >
                                  <FiTrash2 size={14} />
                                </button>
                             </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-3 border-t flex justify-end">
               <p className="text-[10px] text-gray-400 italic">Manage your document categories here. Changes are saved instantly.</p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal for Document Type */}
      {showDeleteTypeConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[270] animate-in fade-in duration-200 backdrop-blur-[1px]">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 transform animate-in slide-in-from-bottom-4 duration-300 border-none">
            <div className="flex items-center gap-3 mb-4">
               <div className="bg-red-100 p-2 rounded-full">
                  <FiAlertTriangle className="text-red-600 text-xl" />
               </div>
               <h3 className="text-lg font-bold text-gray-900 leading-tight">Delete Document Type?</h3>
            </div>
            <p className="text-gray-500 mb-6 text-xs leading-relaxed">
              Are you sure you want to remove the category <span className="font-bold text-gray-700">"{typeToDelete?.type_name}"</span>? 
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                   setShowDeleteTypeConfirm(false);
                   setTypeToDelete(null);
                }}
                className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold text-xs transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmTypeDeletion}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold text-xs shadow-md shadow-red-100 transition-all active:scale-95"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

export default DocumentationForm;
